import {
  CheckCircleOutlined as CheckCircleOutlineIcon,
  PriorityHigh as PriorityHighIcon,
  WarningAmber as WarningAmberIcon,
  InfoOutlined as InfoOutlinedIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { alpha, Box, Button, Collapse, Divider, IconButton, Snackbar, SnackbarContent, Typography, useTheme, type SnackbarCloseReason, type SvgIcon, } from '@pulse/ui';
import React, { useState, type ReactNode } from 'react';

export type SnackbarSeverity = 'success' | 'error' | 'warning' | 'info';

export interface SnackbarAction {
  label: string;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export interface CustomSnackbarProps {
  open: boolean;
  onClose: (event: React.SyntheticEvent | Event, reason: SnackbarCloseReason | 'action') => void;
  message: ReactNode;
  details?: ReactNode;
  severity?: SnackbarSeverity;
  autoHideDuration?: number | null;
  anchorOriginVertical?: 'top' | 'bottom';
  anchorOriginHorizontal?: 'left' | 'center' | 'right';
  actions?: SnackbarAction[];
  defaultExpanded?: boolean;
}

export interface SeverityConfig {
  Icon: typeof SvgIcon;
  color: string;
  bgColor: string;
  borderColor: string;
}

const SEVERITY_ICONS: Record<SnackbarSeverity, typeof SvgIcon> = {
  success: CheckCircleOutlineIcon,
  error: PriorityHighIcon,
  warning: WarningAmberIcon,
  info: InfoOutlinedIcon,
};

// Stable reference so an omitted `actions` prop doesn't create a new array every render.
const EMPTY_ACTIONS: SnackbarAction[] = [];

export const CustomSnackbar = ({
  open,
  onClose,
  message,
  details,
  severity = 'info',
  autoHideDuration = 6000,
  anchorOriginVertical = 'bottom',
  anchorOriginHorizontal = 'center',
  actions = EMPTY_ACTIONS,
  defaultExpanded = false,
}: CustomSnackbarProps) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);

  // Freeze the displayed content while `open` is true, and keep showing that snapshot while
  // closing/transitioning out — a parent that resets its message/severity state in the same
  // update as closing (e.g. clearing both an error and a success string to null) must not be
  // able to flip the visible severity mid-close.
  const [content, setContent] = useState({ message, details, severity, actions });
  if (
    open &&
    (message !== content.message ||
      details !== content.details ||
      severity !== content.severity ||
      actions !== content.actions)
  ) {
    setContent({ message, details, severity, actions });
  }

  const palleteColor = theme.palette[content.severity];
  const color = palleteColor.main;
  const bgColor = `color-mix(in srgb, ${palleteColor.main} 20%, ${theme.palette.background.paper})`;
  const borderColor = alpha(color, 0.4);

  const SeverityIcon = SEVERITY_ICONS[content.severity];

  const handleClose = (event: React.SyntheticEvent | Event, reason: SnackbarCloseReason | 'action') => {
    if (reason === 'clickaway') return;
    setExpanded(false);
    onClose(event, reason);
  };

  const toggleExpanded = () => setExpanded((prev) => !prev);

  const messageNode = (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, gap: 1 }}>
        <SeverityIcon sx={{ fontSize: 20, color, flexShrink: 0 }} />
        <Typography
          variant="body1"
          sx={{
            flex: 1,
            fontWeight: 500,
            color: content.severity === 'error' ? color : 'text.primary',
            lineHeight: 1.4,
            wordBreak: 'break-word',
          }}
        >
          {content.message}
        </Typography>
        {content.details && (
          <IconButton
            size="small"
            onClick={toggleExpanded}
            aria-label={expanded ? 'collapse details' : 'expand details'}
            sx={{ ml: 0.5, color: 'text.secondary', p: '4px' }}
          >
            {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        )}
        <IconButton
          size="small"
          onClick={(e) => handleClose(e, 'escapeKeyDown')}
          aria-label="close"
          sx={{ color: 'text.secondary', p: '4px' }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {content.details && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Divider sx={{ my: 1, borderColor }} />
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, fontSize: '0.8rem' }}>
            {content.details}
          </Typography>

          {content.actions.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
              {content.actions.map(({ label, onClick }) => (
                <Button
                  key={label}
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    onClick(e);
                    handleClose(e, 'action');
                  }}
                  sx={{
                    borderColor,
                    color,
                    fontSize: '0.75rem',
                    py: 0.25,
                    px: 1.5,
                    '&:hover': { borderColor: color, backgroundColor: alpha(color, 0.08) },
                  }}
                >
                  {label}
                </Button>
              ))}
            </Box>
          )}
        </Collapse>
      )}
    </Box>
  );

  return (
    <Snackbar
      open={open}
      onClose={handleClose}
      autoHideDuration={autoHideDuration}
      anchorOrigin={{ vertical: anchorOriginVertical, horizontal: anchorOriginHorizontal }}
    >
      <SnackbarContent
        message={messageNode}
        sx={{
          backgroundColor: bgColor,
          border: `1px solid ${borderColor}`,
          borderRadius: '10px',
          boxShadow: `0 4px 14px ${alpha(theme.palette.common.black, 0.1)}`,
          minWidth: { xs: 260, sm: 380, md: 360 },
          maxWidth: { xs: 288, sm: 440, md: 500 },
          padding: theme.spacing(1.2, 2),
          '& .MuiSnackbarContent-message': { padding: 0, width: '100%' },
        }}
      />
    </Snackbar>
  );
};
